/**
 * `<ContrastWarning />` — Story 5.4 (UX §2.3 lines 139-150, AC line 1016).
 *
 * Inline AA-violation warning shown next to the brand-color picker in
 * Story 6.5's event-create wizard. Pure render primitive — does NOT
 * call `meetsAA` from `web/src/lib/contrast.ts`. The consumer (the
 * wizard) computes the state and passes it in; ContrastWarning just
 * renders the appropriate UX-spec copy + swatch.
 *
 * Three states (matches UX §2.3 outcomes):
 *   - `passes`  — invisible (returns null). Lets the consumer wrap
 *                 every picker in a single ContrastWarning regardless
 *                 of compliance state.
 *   - `warning` — yellow chip. Requires `failingTheme: 'dark' | 'light'`.
 *                 Microcopy: "Your brand color won't meet AA contrast in
 *                 {failingTheme}. We'll automatically use a darkened/
 *                 lightened variant on that theme — preview shown."
 *   - `error`   — red chip. Requires `suggestedColor: string`. Microcopy:
 *                 "This color is too low-contrast for accessible text.
 *                 Please pick a darker/lighter variant — try this:" +
 *                 inline swatch of the suggested compliant color.
 */
import type { CSSProperties } from 'react';

export type ContrastWarningState = 'passes' | 'warning' | 'error';

export interface ContrastWarningProps {
  state: ContrastWarningState;
  /** Required when state="warning". */
  failingTheme?: 'dark' | 'light';
  /** Required when state="error". */
  suggestedColor?: string;
}

const CHIP_BASE_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-sm-size)',
};

/**
 * `ContrastWarning` — inline AA-violation chip.
 */
export function ContrastWarning({
  state,
  failingTheme,
  suggestedColor,
}: ContrastWarningProps) {
  if (state === 'passes') {
    return null;
  }

  if (state === 'warning') {
    return (
      <div
        role="status"
        data-state="warning"
        data-testid="contrast-warning"
        style={{
          ...CHIP_BASE_STYLE,
          background: 'var(--warning)',
          color: 'var(--text-on-brand)',
        }}
      >
        Your brand color won&apos;t meet AA contrast in {failingTheme ?? 'this'}.
        We&apos;ll automatically use a darkened/lightened variant on that theme -
        preview shown.
      </div>
    );
  }

  // state === 'error'
  return (
    <div
      role="alert"
      data-state="error"
      data-testid="contrast-warning"
      style={{
        ...CHIP_BASE_STYLE,
        background: 'var(--danger)',
        color: 'var(--text-on-brand)',
      }}
    >
      <span>
        This color is too low-contrast for accessible text. Please pick a
        darker/lighter variant - try this:
      </span>
      {suggestedColor && (
        <span
          data-testid="contrast-warning-suggested"
          style={{
            display: 'inline-block',
            width: 24,
            height: 24,
            borderRadius: 'var(--radius-sm)',
            background: suggestedColor,
            border: '1px solid var(--text-on-brand)',
          }}
          aria-label={`Suggested color ${suggestedColor}`}
        />
      )}
    </div>
  );
}
